import React, { createContext, useState, useEffect, useContext } from "react";
import apiService from "../services/apiService";
import { useOrganization } from "./OrganizationContext";

const ProjectContext = createContext();

const getProjectStorageKey = (orgId) => `selectedProjectId:${String(orgId)}`;
const LEGACY_PROJECT_STORAGE_KEY = "selectedProjectId";

export const ProjectProvider = ({ children }) => {
  const { currentOrganization } = useOrganization();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load projects when organization is available
  useEffect(() => {
    const loadProjects = async () => {
      if (!currentOrganization?.id) {
        console.log('[ProjectContext] No organization available yet');
        setProjects([]);
        setSelectedProject(null);
        setLoading(false);
        return;
      }

      console.log('[ProjectContext] Loading projects for organization:', currentOrganization.id);
      const storageKey = getProjectStorageKey(currentOrganization.id);

      try {
        setLoading(true);
        setProjects([]);
        setSelectedProject(null);

        const projectsList = await apiService.getProjects(currentOrganization.id);
        console.log('[ProjectContext] Loaded projects:', projectsList);
        setProjects(projectsList);

        // Auto-select project from organization-scoped localStorage or first project
        const savedProjectId =
          localStorage.getItem(storageKey) ||
          localStorage.getItem(LEGACY_PROJECT_STORAGE_KEY);
        console.log('[ProjectContext] Saved project ID from localStorage:', savedProjectId);
        let projectToSelect = null;

        if (savedProjectId) {
          // Convert savedProjectId to number if the project IDs are numbers
          projectToSelect = projectsList.find(p => String(p.id) === String(savedProjectId));
          console.log('[ProjectContext] Found saved project:', projectToSelect);
        }

        // If saved project not found or doesn't exist, select first project
        if (!projectToSelect && projectsList.length > 0) {
          projectToSelect = projectsList[0];
          console.log('[ProjectContext] Auto-selecting first project:', projectToSelect);
        }

        if (projectToSelect) {
          setSelectedProject(projectToSelect);
          localStorage.setItem(storageKey, projectToSelect.id);
          localStorage.removeItem(LEGACY_PROJECT_STORAGE_KEY);
          console.log('[ProjectContext] Selected project:', projectToSelect);
        } else {
          localStorage.removeItem(storageKey);
          console.log('[ProjectContext] No projects available to select');
        }
      } catch (error) {
        console.error("[ProjectContext] Error loading projects:", error);
        setProjects([]);
        setSelectedProject(null);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [currentOrganization]);

  // Function to select a project
  const selectProject = (project) => {
    console.log('[ProjectContext] selectProject called with:', project);
    setSelectedProject(project);
    if (project) {
      if (currentOrganization?.id) {
        localStorage.setItem(getProjectStorageKey(currentOrganization.id), project.id);
      }
      localStorage.removeItem(LEGACY_PROJECT_STORAGE_KEY);
    } else {
      if (currentOrganization?.id) {
        localStorage.removeItem(getProjectStorageKey(currentOrganization.id));
      }
      localStorage.removeItem(LEGACY_PROJECT_STORAGE_KEY);
    }
  };

  // Function to add a new project (after creation)
  const addProject = (project) => {
    console.log('[ProjectContext] addProject called with:', project);
    setProjects(prevProjects => [...prevProjects, project]);
    selectProject(project);
  };

  // Function to reload projects
  const reloadProjects = async () => {
    try {
      if (!currentOrganization?.id) {
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      const storageKey = getProjectStorageKey(currentOrganization.id);
      const projectsList = await apiService.getProjects(currentOrganization.id);
      setProjects(projectsList);
      
      // Re-select current project if it still exists in this organization
      const updatedProject = selectedProject
        ? projectsList.find((p) => String(p.id) === String(selectedProject.id))
        : null;
      if (updatedProject) {
        setSelectedProject(updatedProject);
        localStorage.setItem(storageKey, updatedProject.id);
      } else if (projectsList.length > 0) {
        setSelectedProject(projectsList[0]);
        localStorage.setItem(storageKey, projectsList[0].id);
      } else {
        setSelectedProject(null);
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.error("Error reloading projects:", error);
    }
  };

  const value = {
    projects,
    selectedProject,
    loading,
    selectProject,
    addProject,
    reloadProjects
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject must be used within a ProjectProvider");
  }
  return context;
};

export default ProjectContext;

